import React from 'react'
import PropTypes from 'prop-types'
import ProgressBar from 'src/ui/ProgressBar/ProgressBar'
import { t } from 'src/lib/i18n-react'
import { usePageParams } from 'src/utils/params'

import List from 'src/ui/List/List'
import routes from 'src/lib/rails-routes/rails-routes'

import UnitExercisesPreview from './subcomponents/UnitExercisesPreview/UnitExercisesPreview'
import UnitHeader from './subcomponents/UnitHeader/UnitHeader'
import UnitDetailSkeleton from './UnitDetailSkeleton'
import vocabularyImgUrl from './assets/vocabulary.svg'
import conceptImgUrl from './assets/concept.svg' // ToDo: Use icon from 'ui/assets/
import mediaImgUrl from './assets/media.svg'
import writingPracticeImgUrl from './assets/writing-practice.svg'

const getActivities = (unit, inCourse) => {
  const {
    progress: {
      countOfConceptsSeen,
      countOfMediaSeen,
      countOfVocabularySeen,
      countOfWritingsCompleted,
    },
    countOfVocabulary,
    countOfMedia,
    countOfConcepts,
    countOfWritingPrompts,
  } = unit

  // To-Do: translate tooltips with smart count
  const getDisplay = (count) => {
    return count === 0 ? 'none' : null
  }
  return [
    {
      id: '1',
      routeHelper: routes.advanced_unit_vocabulary,
      name: t('Vocabulary'),
      iconUrl: vocabularyImgUrl,
      display: getDisplay(countOfVocabulary),
      progress: {
        currentProgress: countOfVocabularySeen,
        allProgress: countOfVocabulary,
      },
      tooltip: inCourse
        ? t(
            'You have learned %{smart_count} vocabulary item |||| You have learned %{smart_count} vocabulary items',
            {
              smart_count: countOfVocabularySeen,
            }
          )
        : undefined,
    },
    {
      id: '2',
      routeHelper: routes.advanced_unit_media,
      name: t('Media'),
      iconUrl: mediaImgUrl,
      display: getDisplay(countOfMedia),
      progress: {
        currentProgress: countOfMediaSeen,
        allProgress: countOfMedia,
      },
      tooltip: inCourse
        ? t(
            'You have completed %{smart_count} media activity |||| You have completed %{smart_count} media activities',
            {
              smart_count: countOfMediaSeen,
            }
          )
        : undefined,
    },
    {
      id: '3',
      routeHelper: routes.advanced_unit_concepts,
      name: t('Concepts'),
      iconUrl: conceptImgUrl,
      display: getDisplay(countOfConcepts),
      progress: {
        currentProgress: countOfConceptsSeen,
        allProgress: countOfConcepts,
      },
      tooltip: inCourse
        ? t(
            'You have completed %{smart_count} concept |||| You have completed %{smart_count} concepts',
            {
              smart_count: countOfConceptsSeen,
            }
          )
        : undefined,
    },
    {
      id: '4',
      routeHelper: routes.advanced_unit_writing,
      name: t('Writing Practice'),
      iconUrl: writingPracticeImgUrl,
      display: getDisplay(countOfWritingPrompts),
      progress: {
        currentProgress: countOfWritingsCompleted,
        allProgress: countOfWritingPrompts,
      },
      tooltip: inCourse
        ? t(
            'You have completed %{smart_count} writing activity |||| You have completed %{smart_count} writing activities',
            {
              smart_count: countOfWritingsCompleted,
            }
          )
        : undefined,
    },
  ].filter((item) => item.display !== 'none')
}

const UnitDetail = ({
  unit,
  inCourse,
  showExerciseRating,
  onToggleInCourse,
}) => {
  const { l2Code } = usePageParams()
  const getItemUrl = (item) => item.routeHelper?.(l2Code, unit.id)
  const isQueuable = unit?.topic?.isQueuable

  const renderFooter = (progress) => {
    const { currentProgress, allProgress } = progress
    const completeFraction = (currentProgress / allProgress) * 100
    return (
      <ProgressBar
        completeFraction={completeFraction}
        label={`${currentProgress}/${allProgress}`}
      />
    )
  }

  return (
    <UnitDetailSkeleton
      unitSection={
        <UnitHeader
          unit={unit}
          inCourse={inCourse}
          onToggleInCourse={onToggleInCourse}
        />
      }
      exercisesSection={
        <UnitExercisesPreview
          allUnitExercisesLink={routes.advanced_unit_exercises(l2Code, unit.id)}
          exercises={unit.exercises}
          showExerciseRating={showExerciseRating}
        />
      }
      listSection={
        <List
          title={t('Activities to practice')}
          items={getActivities(unit, inCourse)}
          getItemUrl={getItemUrl}
          size="sm"
          renderFooter={inCourse || !isQueuable ? renderFooter : null}
        />
      }
    />
  )
}

UnitDetail.propTypes = {
  unit: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    iconUrl: PropTypes.string,
    exercises: PropTypes.array.isRequired,
    isElective: PropTypes.bool,
  }).isRequired,
  inCourse: PropTypes.bool.isRequired,
  onToggleInCourse: PropTypes.func.isRequired,
  showExerciseRating: PropTypes.bool.isRequired,
}

export default UnitDetail
